import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowLeftRight, ArrowRight, Contact, Info, Users } from "lucide-react";

import SynopsisSectionCard from "./SynopsisSectionCard";
import TabsComponent from "../../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import type {
  AccountTeamMember,
  CustomerTeamMember,
  ExecutiveTeamMember,
  StakeholderRelationship,
} from "../../../Store/Reducers/AccountDetailsSlice";
import { refreshAccountRelationshipMatrix } from "../../../Store/Reducers/RelationshipSlice";
import {
  ROLES,
  isAccountTeamRole,
  isRole,
} from "../../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../../Utils/Helper/LocalStorage";
import MapThisRelationshipModal from "../../Relationship/account-relationships/map-this-relationship-modal";
import { RelationshipProviders } from "../../Relationship/account-relationships/RelationshipContexts";
import {
  buildPerContactMatrixColumns,
  findPairForMatrixCell,
  formatLevel,
  formatMeddKeyDisplay,
  getRlBadgeClasses,
  meddpiccCanonicalToMatrixKey,
  splitStakeholdersForMatrix,
} from "../../Relationship/account-relationships/relationshipUtils";
import { RlAssessmentModal } from "../../Relationship/account-relationships/rl-assessment-modal";

import "../../Relationship/account-relationships/relationship-prototype-skin.css";

type SynopsisTab =
  | "stakeholder-pairs"
  | "goals-milestone"
  | "playbooks"
  | "stakeholder-engagements";

type SynopsisTabsPanelProps = {
  accountId?: string;
  accountName?: string;
  customerMembers: CustomerTeamMember[];
  accountTeamMembers: AccountTeamMember[];
  executiveSponsors: ExecutiveTeamMember[];
  relationships: StakeholderRelationship[];
  relationshipsLoading: boolean;
  onOpenRelationships: () => void;
  onPairUpdated?: () => void;
};

type PairBucket = "strong" | "moderate" | "poor" | "unassessed";

type DisplayBucket = "strong" | "poor" | "unassessed";

type MatrixEntity = Record<string, unknown>;

type PairRow = {
  id: string;
  vendorId: string | null;
  customerId: string | null;
  vendorName: string;
  customerName: string;
  customerTitle: string;
  customerRole: string;
  isMapped: boolean;
  bucket: PairBucket;
  rl: number | null;
  stakeholder?: MatrixEntity | null;
  contact?: MatrixEntity | null;
  modalPair?: MatrixEntity | null;
};

type AssessmentTarget = {
  pair: MatrixEntity;
  stakeholder: MatrixEntity;
  contact: MatrixEntity;
};

type MapTarget = {
  account: MatrixEntity;
  stakeholder: MatrixEntity;
  contact: MatrixEntity;
};

const SYNOPSIS_TABS = [
  { id: "stakeholder-pairs" as const, label: "Stakeholder Pairs" },
  {
    id: "goals-milestone" as const,
    label: "Goals & Milestones",
    disabled: true,
  },
  { id: "playbooks" as const, label: "Playbooks", disabled: true },
  {
    id: "stakeholder-engagements" as const,
    label: "Stakeholder Engagements",
    disabled: true,
  },
];

const PAIR_COLUMNS: Array<{
  id: DisplayBucket;
  label: string;
  dotClassName: string;
  titleClassName: string;
}> = [
  {
    id: "strong",
    label: "Strong",
    dotClassName: "bg-green-500",
    titleClassName: "text-green-700",
  },
  {
    id: "poor",
    label: "Poor",
    dotClassName: "bg-red-500",
    titleClassName: "text-red-600",
  },
  {
    id: "unassessed",
    label: "Mapped & Unassessed",
    dotClassName: "bg-slate-400",
    titleClassName: "text-slate-500",
  },
];

const PAIR_COLUMN_LIMIT = 4;

const getVendorId = (item: StakeholderRelationship): string | null =>
  item.account_team_member ||
  item.executive_team_member ||
  (item as { vendor_stakeholder?: string }).vendor_stakeholder ||
  null;

/** Short MEDDPICC code for pair cards (EB, DM, TB, …). */
const toMeddShortRole = (role: string | null | undefined): string => {
  if (!role) return "";
  const short = formatMeddKeyDisplay(meddpiccCanonicalToMatrixKey(role));
  return short === "-" ? "" : short;
};

/** Same RL bands as Relationships matrix: Strong 4–5, Moderate 2–3, Poor 0–1, Unassessed. */
const getPairBucket = (rl: number | null | undefined): PairBucket => {
  if (rl == null || !Number.isFinite(rl)) return "unassessed";
  if (rl >= 4) return "strong";
  if (rl >= 2) return "moderate";
  return "poor";
};

const buildPairRowsFromMatrix = (
  stakeholders: MatrixEntity[],
  contacts: MatrixEntity[],
  pairs: MatrixEntity[]
): PairRow[] => {
  const columnDefs = buildPerContactMatrixColumns(contacts);
  const { execTeam, accountTeam } = splitStakeholdersForMatrix(stakeholders);
  const vendors = [...execTeam, ...accountTeam].filter(
    stakeholder => !stakeholder.empty
  );
  const rows: PairRow[] = [];
  const seen = new Set<string>();

  vendors.forEach(stakeholder => {
    columnDefs.forEach(columnDef => {
      const { pair, contact } = findPairForMatrixCell(
        pairs,
        String(stakeholder.id || ""),
        columnDef
      );
      const activeContact = contact || columnDef.contact;
      if (!activeContact?.id) return;

      const key = `${stakeholder.id}:${activeContact.id}`;
      if (seen.has(key)) return;
      seen.add(key);

      const rl =
        pair?.rlLevel != null && Number.isFinite(Number(pair.rlLevel))
          ? Number(pair.rlLevel)
          : null;

      rows.push({
        id: String(pair?.id || key),
        vendorId:
          (pair?.vendorSourceId != null ? String(pair.vendorSourceId) : null) ||
          (stakeholder.sourceId != null ? String(stakeholder.sourceId) : null),
        customerId: String(activeContact.id),
        vendorName: String(stakeholder.name || "Vendor stakeholder"),
        customerName: String(activeContact.name || "Customer contact"),
        customerTitle: String(activeContact.title || ""),
        customerRole: toMeddShortRole(
          String(
            activeContact.meddpiccRoleCanonical ||
              activeContact.meddpiccRole ||
              ""
          )
        ),
        isMapped: Boolean(pair),
        bucket: getPairBucket(rl),
        rl,
        stakeholder,
        contact: activeContact,
        modalPair: pair || null,
      });
    });
  });

  return rows;
};

const formatPairContactSubtitle = (
  title: string,
  role: string
): string | null => {
  const rolePart = role && role !== title ? `(${role})` : null;
  const secondary = [title || null, rolePart].filter(Boolean).join(" ");
  return secondary || null;
};

const LinkBack: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <div className="mt-5 text-right">
    <button
      type="button"
      className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium leading-none text-[#7C3AED] hover:underline"
      onClick={onClick}
    >
      <span>{label.replace(/\s*→\s*$/, "")}</span>
      <ArrowRight
        size={14}
        strokeWidth={2.5}
        className="shrink-0"
        aria-hidden
      />
    </button>
  </div>
);

const TabComingSoon: React.FC<{ title: string; description?: string }> = ({
  title,
  description = "Stay tuned for updates",
}) => (
  <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-gray-200 bg-slate-50/60 px-4 py-12">
    <div className="flex w-full max-w-sm flex-col items-center rounded-xl border border-gray-200 bg-white px-8 py-10 text-center shadow-sm">
      <span className="mb-3 inline-flex size-10 items-center justify-center rounded-full bg-orange-500 text-white">
        <Info size={20} strokeWidth={2.5} aria-hidden />
      </span>
      <p className="m-0 text-base font-semibold text-gray-800">{title}</p>
      <p className="m-0 mt-1.5 text-sm text-gray-400">{description}</p>
    </div>
  </div>
);

const SynopsisTabsPanelContent: React.FC<SynopsisTabsPanelProps> = ({
  accountId,
  accountName,
  customerMembers,
  accountTeamMembers,
  executiveSponsors,
  relationships,
  relationshipsLoading,
  onOpenRelationships,
  onPairUpdated,
}) => {
  const dispatch = useAppDispatch();
  const { organizationSettings } = useAppSelector(state => state.vendorAdmin);
  const [activeTab, setActiveTab] = useState<SynopsisTab>("stakeholder-pairs");
  const [matrixAccount, setMatrixAccount] = useState<MatrixEntity | null>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [assessmentTarget, setAssessmentTarget] =
    useState<AssessmentTarget | null>(null);
  const [mapTarget, setMapTarget] = useState<MapTarget | null>(null);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const executiveId =
    loggedInUser?.executive_id ||
    loggedInUser?.executiveId ||
    loggedInUser?.user_id ||
    loggedInUser?.userId ||
    loggedInUser?.id ||
    null;
  const userRole =
    loggedInUser?.role ||
    loggedInUser?.user?.role ||
    loggedInUser?.user_role ||
    null;

  /** Vendor Admin / Executive Sponsor Lead — Assess & Map on every unassessed card. */
  const canActOnAllUnassessed =
    isRole(userRole, ROLES.VENDOR_ADMIN) ||
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD);

  /** Executive sponsor / account team — only when the card vendor is the logged-in user. */
  const isOwnPairRestrictedRole =
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR) ||
    isRole(userRole, ROLES.EXECUTIVE) ||
    isAccountTeamRole(userRole);

  const currentUserVendorIds = useMemo(() => {
    const ids = new Set<string>();
    const candidates = [
      loggedInUser?.user_id,
      loggedInUser?.userId,
      loggedInUser?.id,
      loggedInUser?.executive_id,
      loggedInUser?.executiveId,
      loggedInUser?.user?.user_id,
      loggedInUser?.user?.id,
    ];
    candidates.forEach(value => {
      const id = String(value || "").trim();
      if (id) ids.add(id);
    });
    return ids;
  }, [loggedInUser]);

  const currentUserVendorNames = useMemo(() => {
    const names = new Set<string>();
    const candidates = [
      loggedInUser?.full_name,
      loggedInUser?.name,
      loggedInUser?.user?.full_name,
      loggedInUser?.user?.name,
    ];
    candidates.forEach(value => {
      const name = String(value || "")
        .trim()
        .toLowerCase();
      if (name) names.add(name);
    });
    return names;
  }, [loggedInUser]);

  const canShowUnassessedAction = useCallback(
    (pair: PairRow) => {
      if (canActOnAllUnassessed) return true;
      if (!isOwnPairRestrictedRole) return false;

      const vendorIds = [
        pair.vendorId,
        pair.modalPair?.vendorSourceId,
        pair.stakeholder?.sourceId,
        pair.stakeholder?.id,
      ]
        .map(value => String(value || "").trim())
        .filter(Boolean)
        .flatMap(id => {
          // Matrix ids look like "exec-123" / "team-123"
          const stripped = id.replace(/^(exec|team)-/, "");
          return stripped && stripped !== id ? [id, stripped] : [id];
        });

      if (vendorIds.some(id => currentUserVendorIds.has(id))) return true;

      const vendorName = String(pair.vendorName || "")
        .trim()
        .toLowerCase();
      return Boolean(vendorName && currentUserVendorNames.has(vendorName));
    },
    [
      canActOnAllUnassessed,
      currentUserVendorIds,
      currentUserVendorNames,
      isOwnPairRestrictedRole,
    ]
  );

  const refreshMatrixAccount = useCallback(
    async (force = true) => {
      if (!accountId) return null;
      setMatrixLoading(true);
      try {
        const refreshed = await dispatch(
          refreshAccountRelationshipMatrix({
            account: {
              id: accountId,
              name: accountName || "Account",
            },
            orgSettings: organizationSettings || {},
            force,
          })
        ).unwrap();
        if (refreshed) {
          setMatrixAccount(refreshed as MatrixEntity);
        }
        return refreshed as MatrixEntity;
      } catch {
        return null;
      } finally {
        setMatrixLoading(false);
      }
    },
    [accountId, accountName, dispatch, organizationSettings]
  );

  useEffect(() => {
    if (!accountId) {
      setMatrixAccount(null);
      return;
    }
    void refreshMatrixAccount(true);
  }, [accountId, refreshMatrixAccount]);

  const handlePairRefresh = useCallback(async () => {
    await refreshMatrixAccount(true);
    onPairUpdated?.();
  }, [onPairUpdated, refreshMatrixAccount]);

  const vendorById = useMemo(() => {
    const map = new Map<string, { name: string; role: string }>();
    accountTeamMembers.forEach(member => {
      map.set(member.user_id, {
        name: member.full_name || member.email,
        role: member.role_name || member.position || "Account Team",
      });
    });
    executiveSponsors.forEach(member => {
      map.set(member.user_id, {
        name: member.full_name || member.email,
        role: member.executive_type
          ? `Exec Sponsor (${member.executive_type})`
          : member.role_name || member.position || "Exec Sponsor",
      });
    });
    return map;
  }, [accountTeamMembers, executiveSponsors]);

  const customerById = useMemo(() => {
    const map = new Map<string, CustomerTeamMember>();
    customerMembers.forEach(member => {
      map.set(member.user_id, member);
    });
    return map;
  }, [customerMembers]);

  const activeRelationships = useMemo(
    () => relationships.filter(item => item.active !== false),
    [relationships]
  );

  const relationshipPairRows = useMemo((): PairRow[] => {
    return activeRelationships.map(item => {
      const vendorId = getVendorId(item);
      const vendor = vendorId ? vendorById.get(vendorId) : null;
      const customer = customerById.get(item.customer_team_member);
      const rl = item.latest_assessment?.rl;
      const bucket = getPairBucket(
        rl != null && Number.isFinite(rl) ? rl : null
      );

      return {
        id: item.relationship_id,
        vendorId,
        customerId: item.customer_team_member || null,
        vendorName: vendor?.name || "Vendor stakeholder",
        customerName: customer?.name || "Customer contact",
        customerTitle: customer?.position || "",
        customerRole: toMeddShortRole(customer?.role),
        isMapped: Boolean(vendorId && item.customer_team_member),
        bucket,
        rl: rl != null && Number.isFinite(rl) ? rl : null,
      };
    });
  }, [activeRelationships, customerById, vendorById]);

  const pairRows = useMemo((): PairRow[] => {
    if (matrixAccount?.stakeholders && matrixAccount?.contacts) {
      return buildPairRowsFromMatrix(
        (matrixAccount.stakeholders as MatrixEntity[]) || [],
        (matrixAccount.contacts as MatrixEntity[]) || [],
        (matrixAccount.pairs as MatrixEntity[]) || []
      );
    }
    return relationshipPairRows;
  }, [matrixAccount, relationshipPairRows]);

  const handleUnassessedAction = useCallback(
    (pair: PairRow) => {
      if (!matrixAccount || !pair.stakeholder || !pair.contact) {
        onOpenRelationships();
        return;
      }
      if (pair.isMapped && pair.modalPair) {
        setAssessmentTarget({
          pair: pair.modalPair,
          stakeholder: pair.stakeholder,
          contact: pair.contact,
        });
        return;
      }
      setMapTarget({
        account: matrixAccount,
        stakeholder: pair.stakeholder,
        contact: pair.contact,
      });
    },
    [matrixAccount, onOpenRelationships]
  );

  const pairsLoading = relationshipsLoading || matrixLoading;

  /** Strong / Poor / Unassessed — each shows top 4 only. Moderate omitted. */
  const displayPairsByBucket = useMemo(() => {
    const strong: PairRow[] = [];
    const poor: PairRow[] = [];
    const unassessed: PairRow[] = [];

    pairRows.forEach(row => {
      if (row.bucket === "strong") strong.push(row);
      else if (row.bucket === "poor") poor.push(row);
      else if (row.bucket === "unassessed" && row.isMapped)
        unassessed.push(row);
    });

    strong.sort((a, b) => (b.rl ?? 0) - (a.rl ?? 0));
    poor.sort((a, b) => (a.rl ?? 0) - (b.rl ?? 0));
    unassessed.sort((a, b) => {
      const aIsCurrentUser =
        a.vendorId != null && currentUserVendorIds.has(a.vendorId);
      const bIsCurrentUser =
        b.vendorId != null && currentUserVendorIds.has(b.vendorId);
      if (aIsCurrentUser !== bIsCurrentUser) {
        return aIsCurrentUser ? -1 : 1;
      }
      return 0;
    });

    return {
      strong: strong.slice(0, PAIR_COLUMN_LIMIT),
      poor: poor.slice(0, PAIR_COLUMN_LIMIT),
      unassessed: unassessed.slice(0, PAIR_COLUMN_LIMIT),
      strongTotal: strong.length,
      poorTotal: poor.length,
      unassessedTotal: unassessed.length,
    };
  }, [currentUserVendorIds, pairRows]);

  const mappedPairCount = useMemo(
    () => pairRows.filter(row => row.isMapped).length,
    [pairRows]
  );

  return (
    <SynopsisSectionCard icon={Contact} title="Account Details" hideHeader>
      <div className="-mx-4 -mt-5 flex flex-wrap items-end justify-between gap-2 border-b border-gray-200 px-2 pt-1">
        <div className="min-w-0 flex-1">
          <TabsComponent
            tabs={SYNOPSIS_TABS}
            activeTab={activeTab}
            onTabChange={(id: SynopsisTab) => setActiveTab(id)}
            variant="tab-underline"
          />
        </div>
        {activeTab === "stakeholder-pairs" ? (
          <button
            type="button"
            className="mb-2 inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 text-xs font-semibold leading-none text-[#7C3AED] hover:underline"
            onClick={onOpenRelationships}
          >
            View all {mappedPairCount}{" "}
            {mappedPairCount === 1 ? "pair" : "pairs"} → Relationships ·
            Stakeholder Map
          </button>
        ) : null}
      </div>

      <div className="pt-4">
        {activeTab === "stakeholder-pairs" ? (
          <div>
            {pairsLoading ? (
              <p className="m-0 text-sm text-gray-400">
                Loading stakeholder pairs…
              </p>
            ) : pairRows.length > 0 ? (
              <div>
                <div className="mb-3 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
                  <Info
                    size={14}
                    strokeWidth={2.25}
                    className="shrink-0 text-[#7C3AED]"
                    aria-hidden
                  />
                  <p className="m-0 text-xs leading-snug text-slate-600">
                    Showing the top {PAIR_COLUMN_LIMIT} pairs per category
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {PAIR_COLUMNS.map(column => {
                    const rows = displayPairsByBucket[column.id];
                    const total =
                      column.id === "strong"
                        ? displayPairsByBucket.strongTotal
                        : column.id === "poor"
                          ? displayPairsByBucket.poorTotal
                          : displayPairsByBucket.unassessedTotal;
                    return (
                      <div
                        key={column.id}
                        className="min-w-0 rounded-lg border border-gray-200 p-3"
                      >
                        <div
                          className={`mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${column.titleClassName}`}
                        >
                          <span
                            className={`inline-block size-2 rounded-full ${column.dotClassName}`}
                          />
                          {column.label} ({total})
                        </div>
                        <div className="flex flex-col gap-2.5">
                          {rows.length > 0 ? (
                            rows.map(pair => (
                              <div
                                key={pair.id}
                                className={`flex items-center gap-2 rounded-lg border px-3.5 py-3 ${
                                  pair.bucket === "unassessed"
                                    ? "border-amber-200 bg-[rgb(255,253,247)]"
                                    : "border-gray-200 bg-white shadow-sm"
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div
                                    className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900"
                                    title={pair.vendorName}
                                  >
                                    {pair.vendorName}
                                  </div>
                                  {(() => {
                                    const secondary = formatPairContactSubtitle(
                                      pair.customerTitle,
                                      pair.customerRole
                                    );
                                    return (
                                      <div
                                        className="mt-1 flex items-center gap-1 text-xs leading-snug"
                                        title={
                                          secondary
                                            ? `↔ ${pair.customerName} · ${secondary}`
                                            : `↔ ${pair.customerName}`
                                        }
                                      >
                                        <ArrowLeftRight
                                          size={12}
                                          strokeWidth={2}
                                          className="shrink-0 text-gray-400"
                                          aria-hidden
                                        />
                                        <span className="min-w-0 line-clamp-2">
                                          <span className="font-medium text-gray-900">
                                            {pair.customerName}
                                          </span>
                                          {secondary ? (
                                            <>
                                              <span className="font-normal text-gray-400">
                                                {" "}
                                                ·{" "}
                                              </span>
                                              <span className="font-normal text-gray-500">
                                                {secondary}
                                              </span>
                                            </>
                                          ) : null}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                                {pair.bucket === "unassessed" &&
                                canShowUnassessedAction(pair) ? (
                                  <button
                                    type="button"
                                    className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold leading-tight text-[#7C3AED] hover:underline"
                                    onClick={() => handleUnassessedAction(pair)}
                                  >
                                    {pair.isMapped ? "Assess" : "Map & Assess"}
                                  </button>
                                ) : pair.bucket !== "unassessed" &&
                                  pair.rl != null ? (
                                  <span
                                    className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${getRlBadgeClasses(pair.rl)}`}
                                  >
                                    {formatLevel("RL", pair.rl)}
                                  </span>
                                ) : null}
                              </div>
                            ))
                          ) : (
                            <div className="flex min-h-[11.5rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-slate-50/50 px-3.5 py-6 text-center">
                              <span className="inline-flex size-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                                <Users
                                  size={18}
                                  strokeWidth={1.75}
                                  aria-hidden
                                />
                              </span>
                              <span className="text-xs font-medium text-gray-400">
                                No pairs
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="m-0 text-sm font-medium text-gray-700">
                  No stakeholder pairs mapped
                </p>
                <p className="m-0 mt-1 text-sm text-gray-400">
                  Map vendor and customer stakeholders in Relationships.
                </p>
                <LinkBack
                  label="Open Relationships →"
                  onClick={onOpenRelationships}
                />
              </div>
            )}
          </div>
        ) : null}

        {activeTab === "goals-milestone" ? (
          <TabComingSoon
            title="Goals & Milestones — Coming Soon!"
            description="Goal tracking and milestones for this account will appear here."
          />
        ) : null}

        {activeTab === "playbooks" ? (
          <TabComingSoon
            title="Playbooks — Coming Soon!"
            description="Account playbooks and recommended actions will appear here."
          />
        ) : null}

        {activeTab === "stakeholder-engagements" ? (
          <TabComingSoon
            title="Stakeholder Engagements — Coming Soon!"
            description="Stakeholder engagement insights for this account will appear here."
          />
        ) : null}
      </div>

      <RlAssessmentModal
        open={Boolean(assessmentTarget)}
        pair={assessmentTarget?.pair}
        stakeholder={assessmentTarget?.stakeholder}
        contact={assessmentTarget?.contact}
        executiveId={executiveId}
        onClose={() => setAssessmentTarget(null)}
        onSubmitted={async () => {
          setAssessmentTarget(null);
          await handlePairRefresh();
        }}
      />
      <MapThisRelationshipModal
        open={Boolean(mapTarget)}
        account={mapTarget?.account || matrixAccount}
        stakeholder={mapTarget?.stakeholder}
        contact={mapTarget?.contact}
        pairs={(matrixAccount?.pairs as MatrixEntity[]) || []}
        stakeholders={(matrixAccount?.stakeholders as MatrixEntity[]) || []}
        contacts={(matrixAccount?.contacts as MatrixEntity[]) || []}
        onClose={() => setMapTarget(null)}
        onOpenCoverageMap={() => {
          setMapTarget(null);
          onOpenRelationships();
        }}
        onMapped={async ({
          stakeholder,
          contact,
        }: {
          stakeholder: MatrixEntity;
          contact: MatrixEntity;
        }) => {
          setMapTarget(null);
          const refreshed = await refreshMatrixAccount(true);
          onPairUpdated?.();
          if (!refreshed) return;
          const columnDefs = buildPerContactMatrixColumns(
            (refreshed.contacts as MatrixEntity[]) || []
          );
          const col = columnDefs.find(
            column =>
              column.contact?.id === contact.id ||
              (column.contacts || []).some(
                (item: MatrixEntity) => item.id === contact.id
              )
          );
          if (!col) return;
          const { pair, contact: activeContact } = findPairForMatrixCell(
            (refreshed.pairs as MatrixEntity[]) || [],
            String(stakeholder.id || ""),
            col
          );
          if (pair) {
            setAssessmentTarget({
              pair,
              stakeholder,
              contact: activeContact || contact,
            });
          }
        }}
      />
    </SynopsisSectionCard>
  );
};

const SynopsisTabsPanel: React.FC<SynopsisTabsPanelProps> = props => (
  <RelationshipProviders>
    <SynopsisTabsPanelContent {...props} />
  </RelationshipProviders>
);

export default SynopsisTabsPanel;
